import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAbsenceRequests, useGetLeaveSettings } from '../../../Core/Hooks/useTimereport';
import { TimereportLeaveActivitySettings } from '../../../Core/Types/admin/timereport';
import './LeaveApplicationList.scss';

type LeaveApplicationListProps = {
	year: number;
};

type LeaveType = 'vacation' | 'parental-leave' | 'flex-withdrawal' | 'leave-of-absence';

type LeaveEntry = {
	id: number;
	date: string;
	hours: number;
	type: LeaveType;
	label: string;
	status: 'approved' | 'pending' | 'denied';
};

const matchesLeaveType = (activityId: number, typeId: number, setting?: TimereportLeaveActivitySettings): boolean => {
	if (!setting || (!setting.activityId && !setting.typeId)) return false;
	return (setting.activityId > 0 && setting.activityId === activityId) || (setting.typeId > 0 && setting.typeId === typeId); //should we compare type ids here??? means we have to remember not to share types between absence activities which might happen - as it has with sick leave below...
};

const formatHours = (value: number): string => {
	return value % 1 === 0 ? String(value) : value.toFixed(2).replace(/0$/, '');
};

const LeaveApplicationList = ({ year }: LeaveApplicationListProps) => {
	const { t } = useTranslation();
	const start = `${year}-01-01`;
	const end = `${year}-12-31`;
	const { timereports } = useGetAbsenceRequests(start, end);
	const { leaveSettings } = useGetLeaveSettings();

	const leaveTypeLabels: Record<LeaveType, string> = {
		vacation: t('TIMEREPORT_USER.OVERVIEW.SHARED.LEAVE_TYPES.VACATION'),
		'parental-leave': t('TIMEREPORT_USER.OVERVIEW.SHARED.LEAVE_TYPES.PARENTAL_LEAVE'),
		'flex-withdrawal': t('TIMEREPORT_USER.OVERVIEW.SHARED.LEAVE_TYPES.WITHDRAWAL_OF_HOURS'),
		'leave-of-absence': t('TIMEREPORT_USER.OVERVIEW.SHARED.LEAVE_TYPES.LEAVE_OF_ABSENCE'),
	};

	const getLeaveType = (activityId: number, typeId: number): LeaveType | null => {
		if (!leaveSettings) return null;
		if (activityId && activityId === leaveSettings.sickLeave?.activityId) return null; // Exclude sick leave as it's not a leave application, needs to be first because it shares typeId with other absences and would get hit further down otherwise.
		if (
			matchesLeaveType(activityId, typeId, leaveSettings.paidVacation) ||
			matchesLeaveType(activityId, typeId, leaveSettings.unpaidVacation) ||
			matchesLeaveType(activityId, typeId, leaveSettings.advanceVacation)
		)
			return 'vacation';
		if (matchesLeaveType(activityId, typeId, leaveSettings.parentalLeave)) return 'parental-leave';
		if (matchesLeaveType(activityId, typeId, leaveSettings.savedHours)) return 'flex-withdrawal';
		if (matchesLeaveType(activityId, typeId, leaveSettings.leaveOfAbsence)) return 'leave-of-absence';
		return null;
	};

	const entries = useMemo(() => {
		const result: LeaveEntry[] = [];

		if (timereports && leaveSettings) {
			timereports.forEach((tr) => {
				const lt = getLeaveType(tr.activityId, tr.typeId);
				if (lt) {
					result.push({
						id: tr.id,
						date: tr.date.split('T')[0],
						hours: tr.units,
						type: lt,
						label: leaveTypeLabels[lt],
						status: tr.isDeleted ? 'denied' : tr.isAttested ? 'approved' : 'pending',
					});
				}
			});
		}

		result.sort((a, b) => a.date.localeCompare(b.date));
		return result;
	}, [timereports, leaveSettings, year, leaveTypeLabels]);

	if (!entries.length) {
		return (
			<div className='biz-leave-applications'>
				<div className='biz-leave-applications__empty'>
					{t('TIMEREPORT_USER.OVERVIEW.LEAVE_APPLICATIONS.EMPTY_FOR_YEAR', { year })}
				</div>
			</div>
		);
	}

	return (
		<div className='biz-leave-applications'>
			<div className='biz-leave-applications__list'>
				{entries.map((entry) => (
					<div key={entry.id} className={`biz-leave-applications__item biz-leave-applications__item--${entry.status}`}>
						<span className='biz-leave-applications__type'>{entry.label}</span>
						<span className='biz-leave-applications__date'>{entry.date}</span>
						<span className='biz-leave-applications__hours'>{formatHours(entry.hours)}h</span>
						<span className={`biz-leave-applications__status biz-leave-applications__status--${entry.status}`}>
							{entry.status === 'pending'
								? t('TIMEREPORT_USER.OVERVIEW.SHARED.STATUS.PENDING')
								: entry.status === 'denied'
									? t('TIMEREPORT_USER.OVERVIEW.SHARED.STATUS.DENIED')
									: t('TIMEREPORT_USER.OVERVIEW.SHARED.STATUS.APPROVED')}
						</span>
					</div>
				))}
			</div>
		</div>
	);
};

export default LeaveApplicationList;
